import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import React from 'react';

import { BulkRunStatusProps } from './BulkRunStatus.props';

const MAX_LISTED_FAILURES = 10;

/**
 * Progress and outcome of a bulk run, shared by every bulk action so they all
 * report the same way. A hundred records takes long enough that a spinner with
 * no count reads as a hang.
 */
export const BulkRunStatus: React.FC<BulkRunStatusProps> = ({
  progress,
  submitting,
  failures = [],
  error,
  verb = 'Updating',
}) => {
  const showProgress = Boolean(submitting && progress && progress.total > 1);
  if (!showProgress && failures.length === 0 && !error) return null;

  return (
    <Box sx={{ mt: 2 }}>
      {showProgress && progress && (
        <Box aria-live="polite">
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
            {verb} job {progress.completed} of {progress.total}…
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(progress.completed / progress.total) * 100}
            aria-label={`${verb} ${progress.completed} of ${progress.total} jobs`}
          />
        </Box>
      )}

      {failures.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1, fontSize: '12px' }}>
          These jobs were not updated:{' '}
          {failures
            .slice(0, MAX_LISTED_FAILURES)
            .map((failure) => failure.id)
            .join(', ')}
          {failures.length > MAX_LISTED_FAILURES
            ? ` and ${failures.length - MAX_LISTED_FAILURES} more`
            : ''}
          .
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, fontSize: '12px' }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
